/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useState } from 'react';
import { X, Minus, Plus, Trash2, ShoppingCart, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';
import EntityIcon, { isUploadedImage } from './EntityIcon';
import { submitInquiry } from '../lib/publicApi';
import { useCart } from '../context/cart-context';
import { formatPrice } from '../lib/productPrice';

// Same best-effort numeric extraction SMEProductsView uses for its price
// filter — Product.price is free text, not a structured amount, so this is
// only ever an estimate shown to the buyer, never a real payable total.
function parsePriceNumbers(raw) {
    if (!raw)
        return [];
    const matches = raw.match(/[\d,]+(?:\.\d+)?/g);
    if (!matches)
        return [];
    return matches.map((m) => parseFloat(m.replace(/,/g, ''))).filter((n) => Number.isFinite(n));
}

// There's no real checkout/payment backend on this platform (see
// InquiryModal) — "placing an order" from the cart submits one reviewed
// Inquiry per supplier, listing every line item and quantity in the
// message, exactly like the existing single-product "Order" flow.
export default function CartDrawer() {
    const { items, isOpen, closeCart, updateQuantity, removeFromCart, clearCart, cartCount } = useCart();
    const [step, setStep] = useState('cart');
    const [form, setForm] = useState({ buyerName: '', buyerEmail: '', buyerPhone: '' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (!isOpen)
        return null;

    const estimatedTotal = items.reduce((sum, it) => {
        const nums = parsePriceNumbers(it.price);
        if (!nums.length)
            return sum;
        return sum + Math.min(...nums) * it.qty;
    }, 0);
    const hasEstimate = items.some((it) => parsePriceNumbers(it.price).length > 0);

    const handleClose = () => { closeCart(); setStep('cart'); setError(null); };

    const handleCheckout = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const bySupplier = new Map();
            items.forEach((it) => {
                const list = bySupplier.get(it.supplierId) || [];
                list.push(it);
                bySupplier.set(it.supplierId, list);
            });
            await Promise.all([...bySupplier.entries()].map(([supplierId, lines]) => submitInquiry({
                supplierId,
                productName: lines[0].name,
                productId: lines[0].id,
                inquiryIntent: 'buy',
                buyerName: form.buyerName,
                buyerEmail: form.buyerEmail,
                buyerPhone: form.buyerPhone,
                message: `Cart order:\n${lines.map((l) => `- ${l.name} x ${l.qty}`).join('\n')}`,
            })));
            clearCart();
            setStep('done');
        }
        catch (err) {
            setError(err.message || 'Could not place your order. Please try again.');
        }
        finally {
            setSubmitting(false);
        }
    };

    return (<div className="fixed inset-0 z-[130] flex justify-end animate-fade-in" id="cart-drawer">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm cursor-pointer" onClick={handleClose}/>
      <div className="relative bg-white w-full max-w-md h-full shadow-2xl z-10 flex flex-col animate-scale-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2">
            <ShoppingCart className="w-4.5 h-4.5 text-indigo-600"/> {step === 'checkout' ? 'Checkout' : `Your Cart (${cartCount})`}
          </h3>
          <button onClick={handleClose} className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer" id="btn-close-cart">
            <X className="w-4 h-4"/>
          </button>
        </div>

        {step === 'done' ? (<div className="flex-1 flex flex-col items-center justify-center text-center px-6 space-y-3">
            <CheckCircle2 className="w-10 h-10 text-emerald-500"/>
            <h4 className="text-sm font-bold text-slate-900">Order request submitted</h4>
            <p className="text-xs text-slate-500 leading-relaxed">Our team reviews every order before it reaches a supplier — you'll be notified once it's approved.</p>
            <button onClick={handleClose} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-xl text-xs font-bold cursor-pointer">Close</button>
          </div>) : items.length === 0 ? (<div className="flex-1 flex flex-col items-center justify-center text-center px-6 space-y-2">
            <ShoppingCart className="w-10 h-10 text-slate-200"/>
            <p className="text-xs text-slate-400">Your cart is empty.</p>
          </div>) : step === 'cart' ? (<>
            <div className="flex-1 overflow-y-auto divide-y divide-slate-50 px-5" id="cart-items-list">
              {items.map((it) => (<div key={it.id} className="py-4 flex gap-3">
                  <span className="w-16 h-16 rounded-xl bg-slate-50 overflow-hidden flex items-center justify-center shadow-inner shrink-0">
                    {isUploadedImage(it.image) ? (<img src={it.image} alt={it.name} referrerPolicy="no-referrer" className="w-full h-full object-cover"/>) : (<EntityIcon value={it.image} alt={it.name} size={32}/>)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-800 truncate">{it.name}</p>
                    <p className="text-[10px] text-slate-400 truncate">{it.supplierName}</p>
                    <p className="text-xs font-bold text-indigo-600 font-mono mt-0.5">{formatPrice(it) || 'Contact for pricing'}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="inline-flex items-center border border-slate-200 rounded-lg overflow-hidden">
                        <button onClick={() => updateQuantity(it.id, it.qty - 1)} className="w-6 h-6 flex items-center justify-center text-slate-500 hover:bg-slate-50 cursor-pointer"><Minus className="w-3 h-3"/></button>
                        <span className="w-8 text-center text-xs font-bold text-slate-800">{it.qty}</span>
                        <button onClick={() => updateQuantity(it.id, it.qty + 1)} className="w-6 h-6 flex items-center justify-center text-slate-500 hover:bg-slate-50 cursor-pointer"><Plus className="w-3 h-3"/></button>
                      </div>
                      <button onClick={() => removeFromCart(it.id)} title="Remove" className="text-slate-300 hover:text-red-500 transition-colors cursor-pointer"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </div>
                </div>))}
            </div>
            <div className="border-t border-slate-100 px-5 py-4 space-y-3 shrink-0">
              {hasEstimate && (<div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500 font-semibold">Estimated total</span>
                  <span className="font-black text-slate-900 font-mono">{estimatedTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                </div>)}
              <button onClick={() => setStep('checkout')} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl text-xs transition-colors cursor-pointer" id="btn-cart-checkout">Proceed to Checkout</button>
              <button onClick={clearCart} className="w-full text-[11px] text-slate-400 hover:text-red-500 font-semibold cursor-pointer">Clear cart</button>
            </div>
          </>) : (<form onSubmit={handleCheckout} className="flex-1 flex flex-col px-5 py-4 space-y-3 overflow-y-auto">
            <p className="text-[11px] text-slate-500 flex items-start gap-1.5 bg-slate-50 border border-slate-100 rounded-xl p-2.5">
              <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-500"/>
              Each supplier receives your order as a reviewed inquiry — our team checks it before it's forwarded.
            </p>
            <input required value={form.buyerName} onChange={(e) => setForm((f) => ({ ...f, buyerName: e.target.value }))} placeholder="Your name" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-indigo-500"/>
            <input required type="email" value={form.buyerEmail} onChange={(e) => setForm((f) => ({ ...f, buyerEmail: e.target.value }))} placeholder="Your email address" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-indigo-500"/>
            <input value={form.buyerPhone} onChange={(e) => setForm((f) => ({ ...f, buyerPhone: e.target.value }))} placeholder="Your phone number (optional)" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs focus:outline-none focus:border-indigo-500"/>
            <div className="flex-1"/>
            {error && <p className="text-[11px] text-red-600 font-semibold">{error}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setStep('cart')} className="flex-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold py-2.5 rounded-xl text-xs transition-colors cursor-pointer">Back</button>
              <button type="submit" disabled={submitting} className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-bold py-2.5 rounded-xl text-xs transition-colors cursor-pointer flex items-center justify-center gap-1.5" id="btn-place-order">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <ShoppingCart className="w-3.5 h-3.5"/>}
                {submitting ? 'Placing order…' : 'Place Order'}
              </button>
            </div>
          </form>)}
      </div>
    </div>);
}
